/**
 * @file src/utils/authTransport.js
 * @description Single source of truth for HOW the session travels.
 *
 *   Two transports exist:
 *
 *   • cookie mode  — the gateway sets an HttpOnly refresh cookie
 *                    (`#attachCookieSession`) and the browser returns it
 *                    automatically. JavaScript never sees the refresh token,
 *                    so an XSS on the dashboard cannot walk away with a
 *                    long-lived credential.
 *   • bearer mode  — the refresh token is handed to the client and kept in
 *                    localStorage.
 *
 *   The gateway sets the cookie in BOTH modes, so bearer mode was not adding
 *   capability — it was only adding exposure. Cookie mode is therefore the
 *   default, and the flag exists to fall back if a deployment needs it.
 *
 *   `persistRefreshToken` is the one place that decides whether the token is
 *   written. Call it instead of `storage.set(KEYS.REFRESH_TOKEN, …)` — three
 *   call sites used to write it unconditionally, which meant the transport
 *   flag did not actually control the thing it was named after.
 *
 *   Admin impersonation (Header, IdentityConsole, UserManager) deliberately
 *   still reads the raw token: it stashes the operator's own session to
 *   restore afterwards, which needs a value JavaScript can hold. That flow
 *   requires bearer mode; see AUTH_TRANSPORT.md notes in the audit.
 */

import { storage } from './storage';

// Build-time guess only. Defaults to bearer, the same default the gateway's
// AUTH_COOKIE_MODE has (and what .env.example ships); docker-compose feeds
// both from that one variable. The guess is overridden by what the server
// says on every login and refresh (`transport` in the session body) — a
// client that assumes cookies against a bearer gateway drops the refresh
// token it was handed, and every session then ends at access-token expiry.
export const COOKIE_AUTH_ENABLED =
  String(import.meta.env.VITE_AUTH_COOKIE_MODE ?? 'false').toLowerCase() === 'true';

const TRANSPORT_KEY = 'auth_transport';
const readServerTransport = () => { try { return storage.get(TRANSPORT_KEY) || null; } catch { return null; } };

/**
 * The transport in force: what the server last said, else the build flag.
 * 'cookie' → the HttpOnly cookie is the credential; 'bearer' → we hold it.
 */
export const cookieAuthActive = () => {
  const said = readServerTransport();
  if (said === 'cookie') return true;
  if (said === 'bearer') return false;
  return COOKIE_AUTH_ENABLED;
};

/** Remember what the server said its transport is (from a session body). */
export const rememberTransport = (transport) => {
  const t = String(transport || '').toLowerCase();
  if (t === 'cookie' || t === 'bearer') { try { storage.set(TRANSPORT_KEY, t); } catch { /* storage unavailable */ } }
};

export const CSRF_COOKIE_NAME = import.meta.env.VITE_CSRF_COOKIE_NAME || 'moksy_csrf';

/**
 * Persist the refresh token only when this deployment actually needs it in
 * JavaScript. In cookie mode we drop it on the floor: the HttpOnly cookie the
 * gateway just set is the credential, and a second copy in localStorage would
 * be pure downside.
 */
export const persistRefreshToken = (refreshToken, transport = null) => {
  if (transport) rememberTransport(transport);
  if (cookieAuthActive()) {
    // Clear any token left over from a previous bearer-mode session, so
    // flipping the flag doesn't leave a stale credential sitting in storage.
    storage.remove(storage.KEYS.REFRESH_TOKEN);
    return;
  }
  if (refreshToken) storage.set(storage.KEYS.REFRESH_TOKEN, refreshToken);
};

/** The refresh token to send in a request body, if any. Empty in cookie mode. */
export const readRefreshToken = () =>
  cookieAuthActive() ? '' : (storage.get(storage.KEYS.REFRESH_TOKEN) || '');

/**
 * Can we attempt a silent refresh? In cookie mode we always can — the cookie
 * either works or it doesn't, and only the server can tell us which.
 */
export const canAttemptRefresh = () =>
  cookieAuthActive() || Boolean(storage.get(storage.KEYS.REFRESH_TOKEN));
